(() => {
  'use strict';
  if (window.IHMimeeqCart) return;
  const configElement = document.getElementById('ih-mimeeq-cart-config');
  if (!configElement) return;
  const config = JSON.parse(configElement.textContent);
  const send = window.fetch.bind(window);
  const root = config.root.endsWith('/') ? config.root : config.root + '/';
  const messages = config.messages;
  let active = null;
  let state = 'checking';
  let revision = 0;
  let scheduled = null;
  let saving = false;
  let saveTimeout = null;
  let saveResults = new Map();
  let busyUntil = 0;
  const pause = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds));
  const customItem = item => Boolean(item.properties && item.properties._mmqProduct) ||
    item.product_type === 'Custom Design' ||
    (item.vendor === 'Industrial-Home' && /Custom Design Ref/i.test(item.product_title || item.title || ''));

  function inspect(cart) {
    if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart response');
    const items = cart.items.filter(customItem);
    return {
      items,
      pending: items.some(item => {
        const gross = item.original_line_price;
        const net = item.final_line_price;
        return !Number.isFinite(gross) || !Number.isFinite(net) || gross <= 0 || net < 0 ||
          gross - net + 1 < Math.round(gross * 0.10);
      })
    };
  }

  async function request(path, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await send(root + path, {
        credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal
      });
      if (!response.ok) {
        if (response.status === 429) holdRequests(response);
        const error = new Error('Cart request failed: ' + response.status);
        error.status = response.status;
        throw error;
      }
      const cart = await response.json();
      inspect(cart);
      return cart;
    } finally {
      clearTimeout(timeout);
    }
  }

  function holdRequests(response) {
    const seconds = Number(response.headers?.get('Retry-After'));
    busyUntil = Date.now() + Math.max(60000, Math.min(300000, (seconds || 120) * 1000));
  }

  function sections() {
    const elements = [...document.querySelectorAll('.shopify-section--main-cart, cart-drawer')];
    return [...new Set(elements.map(element => element.closest('[id^="shopify-section-"]'))
      .filter(Boolean).map(element => element.id.replace('shopify-section-', '')))].slice(0, 5);
  }

  function renderSections(cart) {
    if (!cart.sections) return false;
    let rendered = false;
    for (const [id, html] of Object.entries(cart.sections)) {
      if (typeof html !== 'string') continue;
      const current = document.getElementById('shopify-section-' + id);
      if (!current) continue;
      const replacement = new DOMParser().parseFromString(html, 'text/html')
        .getElementById('shopify-section-' + id);
      if (!replacement) continue;
      const note = current.querySelector('[name="note"]');
      const noteValue = note && note.value;
      const drawer = current.querySelector('cart-drawer');
      const drawerOpen = drawer && drawer.hasAttribute('open');
      current.replaceWith(replacement);
      const nextNote = replacement.querySelector('[name="note"]');
      if (nextNote && noteValue !== null) nextNote.value = noteValue;
      const nextDrawer = replacement.querySelector('cart-drawer');
      if (drawerOpen && nextDrawer) nextDrawer.setAttribute('open', '');
      rendered = true;
    }
    return rendered;
  }

  function setState(next) {
    state = next;
    const notices = document.querySelectorAll('[data-ih-mimeeq-notice]');
    for (const notice of notices) {
      const text = notice.querySelector('[data-ih-notice-text]');
      const retry = notice.querySelector('[data-ih-mimeeq-retry]');
      notice.dataset.state = next;
      if (text) text.textContent = messages[next === 'ready' ? 'ready' : next === 'failed' ?
        (Date.now() < busyUntil ? 'busy' : 'failed') : 'checking'];
      if (retry) retry.hidden = next !== 'failed';
    }
    const shouldLock = next !== 'ready' && notices.length > 0;
    for (const button of document.querySelectorAll('[name="checkout"]')) {
      if (shouldLock && !button.disabled) {
        button.dataset.ihMimeeqLocked = 'true';
        button.disabled = true;
      } else if (!shouldLock && button.dataset.ihMimeeqLocked === 'true') {
        button.disabled = false;
        delete button.dataset.ihMimeeqLocked;
      }
    }
  }

  async function recalculate() {
    return request('cart/update.js', {
      method: 'POST', headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        attributes: {_ih_nudge: String(Date.now()) + '-' + Math.random().toString(36).slice(2)},
        sections: sections(), sections_url: window.location.pathname
      })
    });
  }

  async function run() {
    if (Date.now() < busyUntil) { setState('failed'); return false; }
    let mustRender = Boolean(document.querySelector('[data-ih-mimeeq-notice][data-state="checking"]'));
    setState('checking');
    const deadline = Date.now() + 45000;
    let cart;
    for (let attempt = 0; attempt < 20 && Date.now() < deadline; attempt++) {
      const startedAt = revision;
      try {
        cart = attempt === 0 ? await request('cart.js') : await recalculate();
        if (startedAt !== revision) continue;
        const result = inspect(cart);
        if (!result.pending) {
          if (result.items.length && mustRender && !cart.sections) cart = await recalculate();
          if (startedAt !== revision) continue;
          if (inspect(cart).pending) continue;
          if (result.items.length && mustRender && !renderSections(cart)) {
            throw new Error('Cart prices could not be refreshed');
          }
          setState('ready');
          return true;
        }
        mustRender = true;
      } catch (error) {
        if (error.status === 429) { setState('failed'); return false; }
        if (Date.now() >= deadline) break;
      }
      await pause(Math.min(500 * 2 ** Math.min(attempt, 3), 4000));
    }
    setState('failed');
    return false;
  }

  function synchronize() {
    if (active) return active;
    active = run().finally(() => { active = null; });
    return active;
  }

  function schedule() {
    clearTimeout(scheduled);
    scheduled = setTimeout(() => synchronize(), 150);
  }

  function saveNotice(message, failed = false) {
    let notice = document.getElementById('ih-mimeeq-save-status');
    if (!notice) {
      notice = document.createElement('div');
      notice.id = 'ih-mimeeq-save-status';
      notice.className = 'ih-mimeeq-save-status';
      notice.setAttribute('role', 'status');
      notice.setAttribute('aria-live', 'polite');
      const text = document.createElement('span');
      const link = document.createElement('a');
      link.href = root + 'cart';
      link.textContent = messages.view_cart;
      notice.append(text, link);
      document.body.append(notice);
    }
    notice.hidden = false;
    notice.dataset.state = failed ? 'failed' : 'checking';
    notice.querySelector('span').textContent = message;
    notice.querySelector('a').hidden = !failed;
  }

  function finishSave(failed) {
    saving = false;
    clearTimeout(saveTimeout);
    if (failed) saveNotice(Date.now() < busyUntil ? messages.busy : messages.save_failed, true);
    else {
      const notice = document.getElementById('ih-mimeeq-save-status');
      if (notice) notice.hidden = true;
    }
  }

  document.addEventListener('mimeeq-add-to-cart', event => {
    if (event.detail && event.detail.basketImplementationType === 'mimeeq') return;
    if (Date.now() < busyUntil) {
      event.preventDefault(); event.stopImmediatePropagation();
      saveNotice(messages.busy, true);
      return;
    }
    if (saving) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    saving = true;
    saveResults = new Map();
    saveNotice(messages.saving);
    document.getElementById('ih-mimeeq-save-status').dataset.step = 'waiting';
    saveTimeout = setTimeout(() => finishSave(true), 90000);
  }, true);

  window.fetch = function(input, options) {
    const value = typeof input === 'string' ? input : input && input.url;
    let pathname;
    try { pathname = new URL(value, window.location.origin).pathname; }
    catch (_) { return send(input, options); }
    const mutation = /\/cart\/(add|change|update|clear)(\.js)?$/.test(pathname);
    const adding = /\/cart\/add(\.js)?$/.test(pathname);
    const method = String((options && options.method) || (input && input.method) || 'GET').toUpperCase();
    if (saving && method === 'POST' && /\/apps\/mimeeqApp\/add-to-cart$/.test(pathname)) {
      const notice = document.getElementById('ih-mimeeq-save-status');
      notice.dataset.step = 'creating';
      return send(input, options).then(response => {
        notice.dataset.step = response.ok ? 'created' : 'rejected';
        if (response.status === 429) holdRequests(response);
        if (!response.ok) finishSave(true);
        return response;
      }).catch(error => {
        notice.dataset.step = 'network-error';
        finishSave(true);
        throw error;
      });
    }
    if (!mutation || method !== 'POST') return send(input, options);
    if (adding && saving) document.getElementById('ih-mimeeq-save-status').dataset.step = 'adding';
    const body = options && options.body;
    const key = adding && typeof body === 'string' && (saving || saveResults.has(body)) ? body : null;
    if (key && saveResults.has(key)) return saveResults.get(key).then(response => response.clone());
    const operation = send(input, options).then(async response => {
      if (adding && saving) {
        const notice = document.getElementById('ih-mimeeq-save-status');
        notice.dataset.result = String(response.status);
        if (!response.ok) {
          if (response.status === 429) { holdRequests(response); finishSave(true); }
          try {
            const error = await response.clone().json();
            notice.dataset.cartError = error.description || error.message || '';
          } catch (_) {}
        }
      }
      if (response.ok) {
        revision++;
        if (adding && saving) {
          finishSave(false);
          if (/\/pages\/product-configuration\/?$/.test(window.location.pathname)) {
            setTimeout(() => window.location.assign(root + 'cart'), 0);
          }
        }
        schedule();
      } else if (key) saveResults.delete(key);
      return response;
    }).catch(error => {
      if (key) saveResults.delete(key);
      if (adding && saving) {
        const notice = document.getElementById('ih-mimeeq-save-status');
        notice.dataset.result = 'network-error';
        notice.dataset.cartError = error.message || '';
        finishSave(true);
      }
      throw error;
    });
    if (key) {
      saveResults.set(key, operation);
      return operation.then(response => response.clone());
    }
    return operation;
  };

  document.addEventListener('mimeeq-shopify-open-error-modal', () => finishSave(true));
  document.addEventListener('click', event => {
    const retry = event.target.closest && event.target.closest('[data-ih-mimeeq-retry]');
    if (retry) { event.preventDefault(); synchronize(); }
    const checkout = event.target.closest && event.target.closest('[name="checkout"], a[href$="/checkout"]');
    if (checkout && state !== 'ready' && document.querySelector('[data-ih-mimeeq-notice]')) {
      event.preventDefault(); event.stopImmediatePropagation();
      synchronize();
    }
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    if (state !== 'ready' && document.querySelector('[data-ih-mimeeq-notice]') &&
        form.matches('form[action$="/cart"]') && (!event.submitter || event.submitter.name === 'checkout')) {
      event.preventDefault(); event.stopImmediatePropagation();
      synchronize();
    }
  }, true);
  for (const name of ['cart:change', 'variant:add', 'cart-drawer:refreshed']) {
    document.addEventListener(name, schedule);
  }
  window.addEventListener('pageshow', event => { if (event.persisted) schedule(); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(); });
  window.IHMimeeqCart = {retry: synchronize};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule, {once: true});
  else schedule();
})();
